// src/encounter/risk/lib/morse-questions.ts
// The 8 Morse Fall Scale questions, verbatim label strings + option order + scores, mirroring
// Flutter. Since PROD-2577 the Risk step's CONTENT comes from the Studio template; this list is
// the WIRE vocabulary — the EncounterMetaData Fall categories (`id`, `title`) the Risk bridge
// (`risk-qr-bridge.ts`) maps the template's questions onto, by position. Its guard test pins the
// seed's labels and scores against this list.
//
// Every question is SINGLE-select: choosing one option is exclusive with its siblings (enforced
// by the caller storing at most one selected score per question — see morse-score.ts).

/** One selectable option on a Morse question — its exact label and the points it contributes. */
export interface MorseOption {
  readonly label: string;
  readonly score: number;
}

/** One of the 8 Morse Fall Scale questions. */
export interface MorseQuestionDef {
  readonly id: string;
  readonly label: string;
  readonly options: readonly MorseOption[];
}

export const MORSE_QUESTIONS: readonly MorseQuestionDef[] = [
  {
    id: 'history-of-falling',
    label: 'History of falling (acute fall within 3-6 months)',
    options: [
      { label: 'Yes', score: 25 },
      { label: 'No', score: 0 },
    ],
  },
  {
    id: 'iv-heparin-saline-lock',
    label: "IV / Heparin / Saline Lock (mark 'Yes' if present)",
    options: [
      { label: 'Yes', score: 20 },
      { label: 'No', score: 0 },
    ],
  },
  {
    id: 'older-than-60',
    label: 'Older than 60 years',
    options: [
      { label: 'Yes', score: 15 },
      { label: 'No', score: 0 },
    ],
  },
  {
    id: 'gait-transferring',
    label: "Gait / Transferring (mark one response / use the highest score)",
    options: [
      { label: 'Impaired', score: 20 },
      { label: 'Weak', score: 10 },
      { label: 'Normal / Bed rest / Immobile', score: 0 },
    ],
  },
  {
    id: 'diagnosis',
    label: 'Diagnosis (more than 1 diagnosis)',
    options: [
      { label: 'Yes', score: 15 },
      { label: 'No', score: 0 },
    ],
  },
  {
    id: 'mental-status',
    label: 'Mental status',
    options: [
      { label: 'Impaired hearing', score: 20 },
      { label: 'Dizziness / Vertigo', score: 15 },
      { label: 'Forgets limitations', score: 15 },
      { label: 'Oriented to own ability', score: 0 },
    ],
  },
  {
    id: 'ambulatory-aid',
    label: 'Ambulatory Aid (mark only one response / use the highest score)',
    options: [
      { label: 'Furniture', score: 30 },
      { label: 'Crutches / Cane / Walker', score: 15 },
      { label: 'None / Bed rest / Wheel chair / Nurse assist', score: 0 },
    ],
  },
  {
    id: 'medication-use',
    label:
      'Medication use (Antihypertensive / Diuretics / Use of anti-convulsants / Use of psychotropics / Anti-anxiety / Narcotics / 2 hours post-sedation / Anesthesia)',
    options: [
      { label: 'Yes', score: 25 },
      { label: 'No', score: 0 },
    ],
  },
] as const;
